import { useEffect } from 'react';

import dynamic from 'next/dynamic';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { RefigerationBanner } from 'src/app/components/Banner/Banners';
import { OverrideContentSecurityPolicy } from 'src/app/components/CSP';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { TextUs } from 'src/app/components/Elements/Location/TextUs';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { LayoutRoutes } from 'src/app/popsicle/router/layoutRoutes';
import { medications } from 'src/app/router/paths';
import { loadAccount, loadPatient, sessionSetCurrentUserFromV3User } from 'src/app/state/actions';
import * as actionTypes from 'src/app/state/actions/types';
import { useDispatch } from 'src/app/state/store';
import { CAPSULE_ICON_STRINGS, GENERIC_REFRIGERATION_MESSAGE, IS_MOBILE } from 'src/consts';

import styles from '../LoggedIn/styles.module.scss';

import type { CheckoutLayoutProps } from './container';

const displayName = 'Checkout';

const CapsuleChat = dynamic<Record<string, never>>(
  () => import('src/app/components/CapsuleChat').then(module => module.CapsuleChat),
  {
    ssr: false,
  }
);

export const CheckoutRenderer = ({ location, shouldShowRefrigerationAlert }: CheckoutLayoutProps) => {
  const dispatch = useDispatch();

  const { isLoggedIn, loggedInUser } = useAuth();

  const { data: user, isSuccess: isLoadUserSuccess } = useUser(
    loggedInUser ? loggedInUser.uuid : '',
    Boolean(loggedInUser)
  );

  useEffect(() => {
    if (!!user) {
      dispatch({
        type: actionTypes.SEGMENT_IDENTIFY,
        accountId: user.accountId,
      });
    }
  }, [dispatch, user]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadUserSuccess && user) {
        await dispatch(sessionSetCurrentUserFromV3User(user));
        if (user.accountId) {
          await dispatch(loadAccount());
          await dispatch(loadPatient());
        }
      }
    };

    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoadUserSuccess]);

  return (
    <div className={classNames(styles.component, 'logged-in', 'layout-checkout')}>
      <OverrideContentSecurityPolicy />
      <div className={classNames(styles.header, styles.desktop)}>
        <div className={styles.leftHeaderSection}>
          <div className={styles.logo}>
            <Link aria-label="Go To Medications Home Page" to={medications.toPath()}>
              <CapsuleIcon data-cy="logo" iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
            </Link>
          </div>
        </div>
      </div>

      <div className={classNames(styles.header, styles.mobile)}>
        <div className={styles.menuButton} />
        <div className={styles.logo}>
          <Link to={medications.toPath()}>
            <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
          </Link>
        </div>

        {IS_MOBILE ? (
          <div className={styles.chatButton}>
            <TextUs>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.system_chat} block />
            </TextUs>
          </div>
        ) : (
          <div className={styles.chatButton} />
        )}
      </div>

      {shouldShowRefrigerationAlert && <RefigerationBanner titleText={GENERIC_REFRIGERATION_MESSAGE} />}

      <div className={styles.contentRow}>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            <LayoutRoutes renderKey="content" displayName={displayName} location={location} />
          </div>
        </div>
      </div>

      {isLoggedIn && <CapsuleChat />}
    </div>
  );
};

CheckoutRenderer.displayName = displayName;
